import React, { SyntheticEvent, useEffect, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Autocomplete, Button, Chip, Grid, TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStyles } from '../../hooks/useStyles';
import { useAuth } from '../../units/auth/AuthProvider';
import { faFilePen, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { api } from '../../api';
import { UserGroup } from '../../api/user-group';
import { useGlobal } from '../../units/global/GlobalProvider';
import { Car } from '../../api/car';
import { regLicensePlateNumber } from '../../data/LicensePlateNumber';
import { useScreenSize } from '../../hooks/useScreenSize';
import { grey } from '@mui/material/colors';

export const useProfilePage = () => {
    const page = usePage();
    const classes = useStyles();
    const { userGroups, setUserGroups, reloadUserGroups } = useGlobal();
    const auth = useAuth();
    const screenSize = useScreenSize();
    const [userId, setUserId] = useState('');
    const [inputAccount, setInputAccount] = useState('');
    const [accountEditable, setAccountEditable] = useState<boolean>(false);
    const [inputPassword, setInputPassword] = useState('');
    const [inputConfirmPassword, setInputConfirmPassword] = useState('');
    const [inputUserName, setInputUserName] = useState('');
    const [inputTel, setInputTel] = useState('');
    const [inputMobile, setInputMobile] = useState('');
    const [inputEmail, setInputEmail] = useState('');
    const [inputAddress, setInputAddress] = useState('');
    const [originUserGroups, setOriginUserGroups] = useState<UserGroup[]>([]);
    const [inputUserGroups, setInputUserGroups] = useState<UserGroup[]>([]);
    const [inputCars, setInputCars] = useState<Car[]>([]);
    const maxCars = 5;
    const [tempCarId, setTempCarId] = useState('');
    const reloadUser = async () => {
        api.user.get({ userId: auth.profile.userId, relations: { userGroups: true, cars: true } }).then((ret) => {
            if (ret.data && ret.data.length > 0) {
                setUserId(ret.data[0].userId || '');
                setInputAccount(ret.data[0].account || '');
                setAccountEditable((ret.data[0].userId || 'userId') == (ret.data[0].account || 'account'));
                setInputPassword('');
                setInputConfirmPassword('');
                setInputUserName(ret.data[0].userName || '');
                setInputTel(ret.data[0].tel || '');
                setInputMobile(ret.data[0].mobile || '');
                setInputEmail(ret.data[0].email || '');
                setInputAddress(ret.data[0].address || '');
                setOriginUserGroups(ret.data[0].userGroups || []);
                setInputCars(ret.data[0].cars || []);
                setTempCarId('');
            }
        });
    };
    const onUpdateUser = async () => {
        const updateUser = async () => {
            page.setIsLoading(true);
            await api.user
                .update({
                    userId: userId,
                    account: inputAccount,
                    password: inputPassword || undefined,
                    userName: inputUserName,
                    tel: inputTel || null,
                    mobile: inputMobile || null,
                    email: inputEmail || null,
                    address: inputAddress || null,
                    userGroups: inputUserGroups,
                    cars: inputCars.filter((v, i, a) => i < maxCars),
                })
                .then((ret) => {
                    if (ret.success) {
                        reloadUser();
                        page.simpleAlert.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                    } else {
                        page.simpleAlert.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                    }
                })
                .finally(() => page.setIsLoading(false));
        };
        if (accountEditable && inputAccount != userId) {
            page.alert.show({
                type: 'warning',
                title: '警告',
                message: `確定將${inputAccount}作為登入帳號？確定後無法再次變更`,
                onConfirm: async () => {
                    updateUser();
                },
            });
        } else {
            updateUser();
        }
    };
    const checkCarRegistered = async (carId: string): Promise<boolean> => {
        const cars = (await api.car.get({ carId: carId, relations: { users: true } }).then((ret) => ret.data || [])) || [];
        const users = cars
            .map((car) => car.users || [])
            .flat()
            .filter((user) => user.userId != auth.profile.userId);
        return users.length > 0;
    };
    const handleInputCarsChange = async (event: SyntheticEvent<Element, Event>, newValue: (string | undefined)[]) => {
        if (newValue.length > maxCars) return;
        if (newValue.length == 0) {
            setInputCars([]);
            return;
        }
        const errMsgs: string[] = [];
        await Promise.all(
            newValue.map(async (newCarId) => {
                if (typeof newCarId == 'string' && !regLicensePlateNumber.test(newCarId)) errMsgs.push(`${newCarId}格式錯誤`);
                if (typeof newCarId == 'string' && (await checkCarRegistered(newCarId))) errMsgs.push(`車號${newCarId}已有車主`);
            }),
        );
        if (errMsgs.length > 0) {
            page.alert.show({
                type: 'error',
                title: '錯誤',
                message: errMsgs.join('\n'),
            });
        } else {
            setInputCars(newValue.map((newCarId) => ({ carId: newCarId })));
        }
        return;
    };
    const onReset = () => {
        setUserId('');
        setInputAccount('');
        setAccountEditable(false);
        setInputPassword('');
        setInputConfirmPassword('');
        setInputUserName('');
        setInputTel('');
        setInputMobile('');
        setInputEmail('');
        setInputAddress('');
        setOriginUserGroups([]);
        setInputUserGroups([]);
        setInputCars([]);
        setTempCarId('');
    };
    useEffect(() => {
        reloadUser();
        reloadUserGroups();
    }, []);
    useEffect(() => {
        setInputUserGroups(
            userGroups.filter((userGroup) =>
                originUserGroups.map((originUserGroup) => originUserGroup.userGroupId).includes(userGroup.userGroupId!),
            ),
        );
    }, [userGroups]);
    useEffect(() => {
        setInputUserGroups(
            userGroups.filter((userGroup) =>
                originUserGroups.map((originUserGroup) => originUserGroup.userGroupId).includes(userGroup.userGroupId!),
            ),
        );
    }, [originUserGroups, userGroups]);
    return {
        page,
        classes,
        auth,
        screenSize,
        alert,
        open,
        userId,
        setUserId,
        inputAccount,
        setInputAccount,
        accountEditable,
        setAccountEditable,
        inputPassword,
        setInputPassword,
        inputConfirmPassword,
        setInputConfirmPassword,
        inputUserName,
        setInputUserName,
        inputTel,
        setInputTel,
        inputMobile,
        setInputMobile,
        inputEmail,
        setInputEmail,
        inputAddress,
        setInputAddress,
        originUserGroups,
        setOriginUserGroups,
        inputUserGroups,
        setInputUserGroups,
        inputCars,
        setInputCars,
        maxCars,
        tempCarId,
        setTempCarId,
        userGroups,
        setUserGroups,
        onUpdateUser,
        checkCarRegistered,
        handleInputCarsChange,
        onReset,
    };
};

interface ProfilePageProps {}

export const ProfilePage = (profilePageProps: ProfilePageProps): React.ReactElement => {
    const props = useProfilePage();
    return (
        <Page {...props.page} title={'個人資料'}>
            <Grid container flexDirection={'row'} height={'100%'}>
                <Grid
                    container
                    item
                    flex={1}
                    flexDirection={'column'}
                    padding={props.screenSize.mdUp ? 4 : 1}
                    sx={{ backgroundColor: grey[100] }}
                >
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-start'} xs={12}>
                            <Grid container item flexDirection={'row'} xs={12} md={6}>
                                <TextField
                                    disabled={![props.accountEditable].every((v) => v)}
                                    value={props.inputAccount}
                                    onChange={(e) => props.setInputAccount(e.target.value.trim())}
                                    variant='outlined'
                                    label={
                                        <>
                                            <span style={{ color: 'red' }}>*</span>
                                            {props.userId == props.inputAccount ? '此為系統預設帳號，建議修改為自訂帳號' : '帳號'}
                                        </>
                                    }
                                    error={props.userId == props.inputAccount}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={12} md={6}>
                                <TextField
                                    value={props.inputUserName}
                                    onChange={(e) => props.setInputUserName(e.target.value)}
                                    variant='outlined'
                                    label={
                                        <>
                                            <span style={{ color: 'red' }}>*</span>
                                            {'使用者名稱'}
                                        </>
                                    }
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={12}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    value={props.inputCars.map((car) => car.carId!)}
                                    onChange={props.handleInputCarsChange}
                                    inputValue={props.tempCarId}
                                    onInputChange={(event, newInputValue) =>
                                        props.inputCars.length <= props.maxCars && props.setTempCarId(newInputValue.toUpperCase())
                                    }
                                    options={[]}
                                    getOptionLabel={(option) => option || ''}
                                    filterSelectedOptions
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={`車牌號碼${
                                                props.tempCarId.length == 0 && props.inputCars.length == 0
                                                    ? '，尚未綁定'
                                                    : props.tempCarId.length > 0 && !regLicensePlateNumber.test(props.tempCarId)
                                                    ? '，請輸入正確格式的車牌號碼，例 ABC-123 或 ABC-1234'
                                                    : ''
                                            }`}
                                            placeholder={`請輸入車牌號碼，最多${props.maxCars}台，超過${props.maxCars}台請洽管理員`}  
                                            error={[
                                                props.tempCarId.length == 0 && props.inputCars.length == 0,
                                                props.tempCarId.length > 0 && !regLicensePlateNumber.test(props.tempCarId),
                                            ].some((v) => v)}
                                            onBlur={async () => {
                                                if (
                                                    props.tempCarId.length > 0 &&
                                                    regLicensePlateNumber.test(props.tempCarId) &&
                                                    !(await props.checkCarRegistered(props.tempCarId))
                                                ) {
                                                    props.setInputCars([...props.inputCars, { carId: props.tempCarId }]);
                                                    props.setTempCarId('');
                                                }
                                            }}
                                        />
                                    )}
                                    renderTags={(tagValue, getTagProps) =>
                                        tagValue.map((option, index) => (
                                            <Chip label={option || ''} {...getTagProps({ index })} onDelete={undefined} />
                                        ))
                                    }
                                    disableClearable={true}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={12} md={6}>
                                <TextField
                                    type={'password'}
                                    autoComplete={'new-password'}
                                    value={props.inputPassword}
                                    onChange={(e) => props.setInputPassword(e.target.value)}
                                    variant='outlined'
                                    label={
                                        <>
                                            {`使用者密碼${
                                                props.inputPassword.length > 0 && props.inputPassword.length < 6 ? '(密碼長度至少6碼)' : ''
                                            }`}
                                        </>
                                    }
                                    error={props.inputPassword.length > 0 && props.inputPassword.length < 6}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={12} md={6}>
                                <TextField
                                    type={'password'}
                                    autoComplete={'new-password'}
                                    value={props.inputConfirmPassword}
                                    onChange={(e) => props.setInputConfirmPassword(e.target.value)}
                                    variant='outlined'
                                    label={
                                        <>
                                            {`再次輸入密碼${
                                                props.inputConfirmPassword.length > 0 && props.inputPassword != props.inputConfirmPassword
                                                    ? '(密碼不一致)'
                                                    : ''
                                            }`}
                                        </>
                                    }
                                    error={props.inputConfirmPassword.length > 0 && props.inputPassword != props.inputConfirmPassword}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={12} md={6}>
                                <TextField
                                    value={props.inputTel}
                                    onChange={(e) => props.setInputTel(e.target.value)}
                                    variant='outlined'
                                    label={<>{'市話'}</>}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={12} md={6}>
                                <TextField
                                    value={props.inputMobile}
                                    onChange={(e) => props.setInputMobile(e.target.value)}
                                    variant='outlined'
                                    label={<>{'手機'}</>}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={12} md={6}>
                                <TextField
                                    value={props.inputEmail}
                                    onChange={(e) => props.setInputEmail(e.target.value)}
                                    variant='outlined'
                                    label={<>{'電子信箱'}</>}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={12} md={6}>
                                <TextField
                                    value={props.inputAddress}
                                    onChange={(e) => props.setInputAddress(e.target.value)}
                                    variant='outlined'
                                    label={<>{'地址'}</>}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid>
                            {/* <Grid container item flexDirection={'row'} xs={12}>
                                <Autocomplete
                                    multiple
                                    value={props.inputUserGroups}
                                    onChange={(event, newValue) => props.setInputUserGroups(newValue)}
                                    options={props.userGroups}
                                    getOptionLabel={(option) => option.descr!}
                                    filterSelectedOptions
                                    renderInput={(props) => <TextField {...props} label='使用者群組' placeholder='搜尋使用者群組' />}
                                    getOptionDisabled={(option) => (option.authority || 0) >= props.auth.profile.authority}
                                    renderTags={(tagValue, getTagProps) =>
                                        tagValue.map((option, index) => (
                                            <Chip
                                                label={option.descr!}
                                                {...getTagProps({ index })}
                                                disabled={(option.authority || 0) >= props.auth.profile.authority}
                                            />
                                        ))
                                    }
                                    disableClearable={true}
                                    style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                />
                            </Grid> */}
                            <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                                <Button
                                    disabled={
                                        ![
                                            props.inputAccount.length > 0,
                                            props.inputUserName.length > 0,
                                            props.inputPassword.length >= 6 || props.inputPassword.length == 0,
                                            props.inputPassword == props.inputConfirmPassword,
                                        ].every((v) => v)
                                    }
                                    className={props.classes.buttonCyan}
                                    style={{ margin: 4, marginTop: 12 }}
                                    onClick={props.onUpdateUser}
                                >
                                    <FontAwesomeIcon icon={faFilePen} fixedWidth />
                                    {'確定更新'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Page>
    );
};
